import { useEffect, useState } from 'react';
import * as Effect from 'effect/Effect';
import * as Stream from 'effect/Stream';
import { pipe } from 'effect/Function';
import { NetworkService, NetworkLayer } from '@/effects/network/network.service';
import { getConnectionQuality, type ConnectionQuality } from '@/effects/network/network.service';

export interface NetworkStatus {
  isOnline: boolean;
  connectionQuality: ConnectionQuality;
}

export function useNetworkStatus() {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: typeof window !== 'undefined' ? navigator.onLine : true,
    connectionQuality: {
      type: 'unknown',
      downlink: 0,
      rtt: 0,
      saveData: false
    }
  });

  useEffect(() => {
    // Get initial status
    Effect.runPromise(
      pipe(
        Effect.all([
          NetworkService.pipe(Effect.flatMap(s => s.isOnline)),
          getConnectionQuality
        ]),
        Effect.provide(NetworkLayer),
        Effect.map(([isOnline, connectionQuality]) => {
          setStatus({ isOnline, connectionQuality });
        })
      )
    );

    // Subscribe to changes
    const subscription = Effect.runPromise(
      pipe(
        NetworkService,
        Effect.flatMap(service =>
          Stream.runForEach(
            service.changes,
            (isOnline) =>
              Effect.all([
                Effect.succeed(isOnline),
                getConnectionQuality
              ]).pipe(
                Effect.map(([online, quality]) => {
                  setStatus({ isOnline: online, connectionQuality: quality });
                })
              )
          )
        ),
        Effect.provide(NetworkLayer)
      )
    );

    return () => {
      // Cleanup subscription
      subscription.then(fiber => {
        (fiber as any)?.interrupt();
      });
    };
  }, []);

  return status;
}

// Hook to check if we should sync based on connection
export function useShouldSync() {
  const { isOnline, connectionQuality } = useNetworkStatus();

  return {
    shouldSync: isOnline && !connectionQuality.saveData && connectionQuality.type !== 'slow-2g',
    reason: !isOnline
      ? 'offline'
      : connectionQuality.saveData
      ? 'save-data'
      : connectionQuality.type === 'slow-2g'
      ? 'slow-connection'
      : null
  };
}